import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Spin } from "antd";

import Footer from "../components/Footer";
import Header from "../components/Header";
import { useSubscription } from "../api/useSubscription";
import { Plan } from "../models";
import { formatPlanPrice } from "../utils/planUtils";
import "./GoogleReviewLinkGenerator.css";
import "./Pricing.css";

type PricingPlan = {
  id: string;
  name: string;
  tagline?: string;
  description: string;
  price: number | null;
  priceLabel?: string;
  period: string;
  features: string[];
  cta: string;
  ctaLink: string;
  featured?: boolean;
  badge?: string;
  comingSoon?: boolean;
};

function mapPlanToPricingPlan(plan: Plan): PricingPlan {
  return {
    id: plan.id,
    name: plan.name,
    tagline: plan.tagline || undefined,
    description: plan.description,
    price: plan.price,
    priceLabel: plan.price_label || undefined,
    period: plan.period,
    features: plan.features,
    cta: plan.cta,
    ctaLink: plan.cta_link,
    featured: plan.featured,
    badge: plan.badge || undefined,
    comingSoon: plan.coming_soon,
  };
}

const FALLBACK_PLANS: PricingPlan[] = [
  {
    id: "free",
    name: "Free",
    tagline: "Forever free",
    description:
      "Quick viral growth for every merchant. Get discovered with an AI review video and a QR code.",
    price: 0,
    period: "month",
    features: [
      "1 Google review video (with industry AI template)",
      "QR code of your Google review video",
      "1 button in your review video page",
    ],
    cta: "Get started free",
    ctaLink: "/google-review-link-generator",
  },
  {
    id: "starter",
    name: "Starter",
    tagline: "Individual merchants",
    description:
      "Our most popular plan for solo businesses ready to drive traffic, conversions, and brand video updates.",
    price: 9.9,
    period: "month",
    featured: true,
    badge: "Most popular",
    features: [
      "Unlimited videos",
      "Remove Vioo logo on your review video page",
      "Up to 2 buttons in your review video page",
      "Up to 5 links (Website, Order, Google Review, Menu, Call)",
      "Custom button colors",
      "Analytics & reporting",
      "Google Review tracking",
    ],
    cta: "Start Starter",
    ctaLink: "/login",
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "Professional merchants",
    description:
      "Built for growing teams that need more buttons, unlimited links, and smarter analytics with AI insights.",
    price: 29,
    period: "month",
    features: [
      "Everything in Starter",
      "Multi-user management",
      "Up to 4 buttons per page",
      "Unlimited pages",
      "Analytics with comparisons & trends",
      "AI-powered performance suggestions",
    ],
    cta: "Start Pro",
    ctaLink: "/login",
  },
];

const PricingPlanCard: React.FC<{ plan: PricingPlan }> = ({ plan }) => {
  const priceDisplay = formatPlanPrice({
    price: plan.price,
    price_label: plan.priceLabel,
    period: plan.period,
  });

  return (
    <article
      className={`pricing-plan${plan.featured ? " pricing-plan--featured" : ""}${plan.comingSoon ? " pricing-plan--coming-soon" : ""}`}
    >
      {plan.badge && <span className="pricing-plan-badge">{plan.badge}</span>}
      <h2 className="pricing-plan-name">{plan.name}</h2>
      {plan.tagline && <p className="pricing-plan-tagline">{plan.tagline}</p>}
      <p className="pricing-plan-desc">{plan.description}</p>

      <div className="pricing-price-block">
        <div className="pricing-price-row">
          <span className="pricing-price">{priceDisplay}</span>
          {plan.price !== null && plan.price > 0 && (
            <span className="pricing-price-period">/ {plan.period}</span>
          )}
          {plan.price === 0 && (
            <span className="pricing-price-period">/ {plan.period}</span>
          )}
          {plan.priceLabel && (
            <span className="pricing-price-period">{plan.period}</span>
          )}
        </div>
      </div>

      <ul className="pricing-features">
        {plan.features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>

      {plan.comingSoon ? (
        <Button
          type="primary"
          size="large"
          block
          disabled
          className="grlg-btn-primary pricing-plan-cta"
        >
          {plan.cta}
        </Button>
      ) : (
        <Link to={plan.ctaLink} className="pricing-plan-cta">
          <Button
            type="primary"
            size="large"
            block
            className="grlg-btn-primary"
          >
            {plan.cta}
          </Button>
        </Link>
      )}
    </article>
  );
};

const Pricing: React.FC = () => {
  const { getPlans } = useSubscription();
  const [plans, setPlans] = useState<PricingPlan[]>(FALLBACK_PLANS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPlans()
      .then((apiPlans) => {
        if (apiPlans.length > 0) {
          setPlans(apiPlans.map(mapPlanToPricingPlan));
        }
      })
      .catch(() => {
        // Keep fallback plans when the API is unavailable.
      })
      .finally(() => setLoading(false));
  }, [getPlans]);

  return (
    <div
      className="pricing-page flex min-h-screen flex-col"
      style={{
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
      }}
    >
      <Header />
      <main className="pricing-main">
        <div className="pricing-hero">
          <p className="pricing-eyebrow">Plans & pricing</p>
          <h1 className="pricing-title">
            <span className="pricing-title-gradient">
              AI Video Business Platform
            </span>
          </h1>
          <p className="pricing-subtitle">
            Vioo is more than a link page — it is an AI system that helps
            merchants drive traffic, improve conversions, and manage brand video
            at scale.
          </p>
        </div>

        <div className="pricing-platform-banner">
          <span className="pricing-platform-badge">Not just Linktree</span>
          <p className="pricing-platform-text">
            You are not buying a simple bio link. You are investing in ongoing
            traffic, conversion tools, and AI-powered video business
            management.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Spin size="large" />
          </div>
        ) : (
          <div className="pricing-grid">
            {plans.map((plan) => (
              <PricingPlanCard key={plan.id} plan={plan} />
            ))}
          </div>
        )}

        <p className="pricing-powered-by">
          Powered by{" "}
          <Link to="/" className="pricing-powered-by-link">
            Vioo.vip
          </Link>
        </p>
      </main>
      <Footer />
    </div>
  );
};

export default Pricing;
